import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../api';
import Select from '../components/Select';
import ItemCard from '../components/ItemCard';
import Field, { FormRow } from '../components/ui/Field';
import EmptyState from '../components/ui/EmptyState';
import Estado from '../components/ui/Estado';
import PageHeader from '../components/ui/PageHeader';
import Modal from '../components/ui/Modal';
import { toast } from '../components/Toast';
import { useConfirm } from '../hooks/useConfirm';
import { fmt, fmtData, agora, paraDateTimeLocal } from '../utils/format';
import { labelUnidadePlural } from '../utils/unidades';
import FormCarga, { FORM_CARGA_VAZIO } from './cargas/FormCarga';
import RelatoriosProducao from './cargas/RelatoriosProducao';

const PAGE_SIZE = 20;

export default function Cargas() {
  // Lista primeiro: colheita é consulta frequente. O formulário abre no modal.
  const [aba, setAba] = useState('lista');
  const [criando, setCriando] = useState(false);
  const [armazens, setArmazens] = useState([]);
  const [safras, setSafras] = useState([]);
  const [usuarios, setUsuarios] = useState([]);
  const [carregandoBase, setCarregandoBase] = useState(true);
  const [erroBase, setErroBase] = useState('');

  const [cargas, setCargas] = useState([]);
  const [carregandoLista, setCarregandoLista] = useState(false);
  const [erroLista, setErroLista] = useState('');
  const [editando, setEditando] = useState(null);
  const [filtroSafra, setFiltroSafra] = useState('');
  const [pagina, setPagina] = useState(0);
  const [totalPaginas, setTotalPaginas] = useState(0);
  const [totalCargas, setTotalCargas] = useState(0);

  const { confirmar, ConfirmDialog } = useConfirm();

  /**
   * Dados que o formulário precisa antes de abrir.
   *
   * A safra vinha com `.catch(() => [])`: com a rede caída a lista chegava vazia
   * e esta tela mostrava "Nenhuma safra aberta" em plena colheita. Agora a falha
   * da safra é erro de verdade; armazém e usuário são opcionais no registro, então
   * seguem tolerando falha.
   */
  const carregarBase = useCallback(async () => {
    setCarregandoBase(true);
    setErroBase('');
    try {
      const [a, s, u] = await Promise.all([
        api.getArmazens().catch(() => []),
        api.getSafras(),
        api.getUsuarios().catch(() => []),
      ]);
      setArmazens(a || []); setSafras(s || []); setUsuarios(u || []);
    } catch (e) {
      setErroBase('Não conseguimos buscar suas safras agora. Confira a internet e tente de novo. Nada foi perdido.');
    } finally {
      setCarregandoBase(false);
    }
  }, []);

  useEffect(() => { carregarBase(); }, [carregarBase]);

  const carregarCargas = useCallback(async (p = 0) => {
    setCarregandoLista(true);
    setErroLista('');
    try {
      const res = await api.listarCargas(p, PAGE_SIZE);
      setCargas(res.content);
      setTotalPaginas(res.totalPages);
      setTotalCargas(res.totalElements);
      setPagina(p);
    } catch (e) {
      setErroLista(e.message || 'Falha ao carregar os registros.');
    } finally {
      setCarregandoLista(false);
    }
  }, []);

  useEffect(() => {
    if (aba === 'lista') carregarCargas(0);
  }, [aba, carregarCargas]);

  const excluir = async (c) => {
    const ok = await confirmar({
      titulo: 'Excluir registro de produção',
      mensagem: <>Excluir o registro de <strong>{c.safraLavoura?.lavoura?.descricao}</strong> de {fmtData(c.dataHoraEntrega)}?</>,
      aviso: 'Essa produção deixa de contar no estoque e nos relatórios da safra.',
      labelConfirmar: 'Excluir',
    });
    if (!ok) return;
    try {
      await api.excluirCarga(c.id);
      toast('Registro excluído.', 'success');
      carregarCargas(pagina);
    } catch (err) { toast(err.message, 'error'); }
  };

  const iniciarEdicao = (c) => {
    setCriando(false);
    setEditando({
      id: c.id,
      dataHoraEntrega: paraDateTimeLocal(c.dataHoraEntrega),
      safraId: String(c.safraLavoura?.safra?.id || ''),
      safraLavouraId: String(c.safraLavoura?.id || ''),
      armazemId: String(c.armazem?.id || ''),
      placaCaminhao: c.placaCaminhao || '',
      pesoBrutoKg: c.pesoBrutoKg || '',
      pesoLiquidoKg: c.pesoLiquidoKg || '',
      taraKg: c.taraKg || '',
      depositadoEm: c.depositadoEm || '',
      notaEmitida: c.notaEmitida || false,
      unidadeProducao: c.unidadeProducao || 'KG',
      quantidade: c.quantidade || '',
      destino: c.destino || '',
      tipoClassificacao: c.tipoClassificacao || '',
      classificacao: c.classificacao || '',
      umidadePct: c.umidadePct || '',
      impurezaPct: c.impurezaPct || '',
    });
  };

  // O filtro por safra é local à página carregada: evita uma ida ao servidor
  // para quem só quer conferir as últimas entregas de uma safra.
  const visiveis = filtroSafra
    ? cargas.filter(c => String(c.safraLavoura?.safra?.id) === String(filtroSafra))
    : cargas;

  const safrasDaPagina = [...new Map(
    cargas.filter(c => c.safraLavoura?.safra).map(c => [c.safraLavoura.safra.id, c.safraLavoura.safra])
  ).values()];

  const abrirNova = () => { setEditando(null); setCriando(true); };
  const fecharForm = () => { setCriando(false); setEditando(null); };

  const cabecalho = (acao) => (
    <PageHeader
      titulo="Colheita"
      icone="colheita"
      descricao="Registrar produção"
      acao={acao}
    />
  );

  if (carregandoBase) {
    return (
      <div>
        {cabecalho()}
        <Estado carregando skeleton="card" itens={4} />
      </div>
    );
  }

  // Buscando, falhou e realmente sem safra aberta são três coisas diferentes:
  // só a última manda o produtor criar safra.
  if (erroBase) {
    return (
      <div>
        {cabecalho()}
        <Estado erro={erroBase} aoTentarNovamente={carregarBase} />
      </div>
    );
  }

  const safrasAbertas = safras.filter(s => s.status !== 'FECHADA');

  if (safrasAbertas.length === 0) {
    return (
      <div>
        {cabecalho()}
        <EmptyState
          icone="colheita"
          titulo="Nenhuma safra aberta"
          descricao="Para registrar produção você precisa de uma safra aberta com pelo menos uma área de cultivo plantada."
          acao={{ to: '/safras', label: 'Ir para Safras / Ciclos' }}
        />
      </div>
    );
  }

  return (
    <div>
      {ConfirmDialog}
      {cabecalho({
        label: 'Registrar produção',
        icone: 'adicionar',
        onClick: abrirNova,
      })}

      <div className="tabs" role="tablist">
        <button
          type="button"
          role="tab"
          aria-selected={aba === 'lista'}
          className={`tab ${aba === 'lista' ? 'active' : ''}`}
          onClick={() => setAba('lista')}
        >
          Registros
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={aba === 'relatorios'}
          className={`tab ${aba === 'relatorios' ? 'active' : ''}`}
          onClick={() => setAba('relatorios')}
        >
          Relatórios
        </button>
      </div>

      {aba === 'relatorios' && (
        <p className="secao-sub" style={{ margin: '0 0 0.75rem' }}>
          Também em{' '}
          <Link to="/relatorios/producao" style={{ color: 'var(--accent)' }}>
            Relatórios → Produção
          </Link>
          {' '}com o restante das análises.
        </p>
      )}

      {aba === 'lista' && (
        <div className="card">
          <div className="secao-titulo">
            <h2>Registros de produção</h2>
            <span className="contador">{totalCargas}</span>
          </div>

          {safrasDaPagina.length > 1 && (
            <FormRow>
              <Field label="Filtrar por safra">
                <Select
                  value={filtroSafra}
                  onChange={setFiltroSafra}
                  options={safrasDaPagina.map(s => ({ value: s.id, label: `${s.cultura || 'Safra'} — ${s.descricao}` }))}
                  placeholder="Todas as safras"
                />
              </Field>
            </FormRow>
          )}

          <Estado
            carregando={carregandoLista}
            erro={erroLista}
            aoTentarNovamente={() => carregarCargas(pagina)}
            vazio={visiveis.length === 0}
            vazioProps={{
              icone: 'colheita',
              titulo: filtroSafra ? 'Nenhum registro nesta safra' : 'Nenhuma produção registrada',
              descricao: filtroSafra
                ? 'Escolha outra safra ou limpe o filtro.'
                : 'Assim que você registrar a primeira colheita, ela aparece aqui.',
              acao: filtroSafra
                ? { onClick: () => setFiltroSafra(''), label: 'Limpar filtro' }
                : { onClick: abrirNova, label: 'Registrar produção' },
            }}
          >
            <>
              {visiveis.map(c => (
                <ItemCard key={c.id} onEdit={() => iniciarEdicao(c)} onDelete={() => excluir(c)}>
                  <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)', minWidth: '70px' }}>
                    {fmtData(c.dataHoraEntrega)}
                  </span>
                  <span style={{ flex: 1, fontWeight: 600, minWidth: '110px' }}>
                    {c.safraLavoura?.lavoura?.tipoArea === 'ESTUFA' ? 'Estufa' : 'Lavoura'}
                    {' — '}{c.safraLavoura?.lavoura?.descricao}
                  </span>
                  <span style={{ fontSize: '0.82rem' }}>
                    <strong>{fmt(c.quantidade ?? c.pesoLiquidoKg)}</strong>{' '}
                    <span style={{ color: 'var(--text-muted)', fontSize: '0.75rem' }}>
                      {labelUnidadePlural(c.unidadeProducao || 'KG')}
                    </span>
                  </span>
                  {c.armazem && (
                    <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>{c.armazem.nome}</span>
                  )}
                  {c.classificacao && (
                    <span className="chip" style={{ cursor: 'default' }}>
                      {c.tipoClassificacao}: {c.classificacao}
                    </span>
                  )}
                </ItemCard>
              ))}

              {totalPaginas > 1 && !filtroSafra && (
                <div className="paginacao">
                  <button
                    className="btn-secondary"
                    disabled={pagina === 0 || carregandoLista}
                    onClick={() => carregarCargas(pagina - 1)}
                  >← Anterior</button>
                  <span className="paginacao-info">Página {pagina + 1} de {totalPaginas}</span>
                  <button
                    className="btn-secondary"
                    disabled={pagina >= totalPaginas - 1 || carregandoLista}
                    onClick={() => carregarCargas(pagina + 1)}
                  >Próxima →</button>
                </div>
              )}
            </>
          </Estado>
        </div>
      )}

      {aba === 'relatorios' && <RelatoriosProducao armazens={armazens} safras={safras} />}

      {(criando || editando) && (
        <Modal
          titulo={editando ? 'Editar registro' : 'Registrar produção'}
          onClose={fecharForm}
          largura={640}
        >
          <FormCarga
            key={editando?.id || 'novo'}
            embutido
            armazens={armazens}
            safras={safras}
            usuarios={usuarios}
            initial={editando || { ...FORM_CARGA_VAZIO, dataHoraEntrega: agora() }}
            onSave={(data) => editando
              ? api.atualizarCarga(editando.id, data)
              : api.criarCarga(data)}
            onCancel={fecharForm}
            onSucesso={() => {
              toast(editando ? 'Registro atualizado.' : 'Produção registrada.', 'success');
              fecharForm();
              if (aba === 'lista') carregarCargas(pagina);
              else carregarCargas(0);
            }}
            submitLabel={editando ? 'Salvar alterações' : 'Registrar produção'}
          />
        </Modal>
      )}
    </div>
  );
}
